import React, { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { dateOnlyBodyTemplate, formatDateForApi } from '../../utils/dateUtils';

// Mock data for demonstration
const mockOrders = [
    {
        id: 1,
        orderNumber: 'ORD-1001',
        customerName: 'Mega Motor A.Ş.',
        orderDate: '2025-03-15',
        status: 'Pending',
        priority: 'High',
        totalAmount: 15000,
        itemCount: 5,
        controlStatus: 'Approved',
        controlDate: '2025-03-16',
        controlledBy: 'Ahmet Yılmaz'
    },
    {
        id: 2,
        orderNumber: 'ORD-1002',
        customerName: 'Anadolu Otomotiv Ltd.',
        orderDate: '2025-03-17',
        status: 'Processing',
        priority: 'Normal',
        totalAmount: 8500,
        itemCount: 3,
        controlStatus: 'Pending',
        controlDate: null,
        controlledBy: null
    },
    {
        id: 3,
        orderNumber: 'ORD-1003',
        customerName: 'Yıldız Makine San.',
        orderDate: '2025-03-18',
        status: 'Completed',
        priority: 'Low',
        totalAmount: 22000,
        itemCount: 7,
        controlStatus: 'Rejected',
        controlDate: '2025-03-19',
        controlledBy: 'Mehmet Kaya'
    }
];

const statusOptions = [
    { name: 'Onaylandı', code: 'Approved' },
    { name: 'Beklemede', code: 'Pending' },
    { name: 'Reddedildi', code: 'Rejected' }
];

const priorityOptions = [
    { name: 'Yüksek', code: 'High' },
    { name: 'Normal', code: 'Normal' },
    { name: 'Düşük', code: 'Low' }
];

const OrderControlReportPage = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        orderNumber: {
            operator: FilterOperator.AND,
            constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }]
        },
        customerName: {
            operator: FilterOperator.AND,
            constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }]
        },
        status: { value: null, matchMode: FilterMatchMode.EQUALS },
        priority: { value: null, matchMode: FilterMatchMode.EQUALS },
        controlStatus: { value: null, matchMode: FilterMatchMode.EQUALS }
    });
    const [displayModal, setDisplayModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [formData, setFormData] = useState({
        orderNumber: '',
        customerName: '',
        orderDate: new Date(),
        status: '',
        priority: '',
        totalAmount: 0,
        itemCount: 0,
        controlStatus: '',
        controlDate: new Date(),
        controlledBy: ''
    });

    useEffect(() => {
        setOrders(mockOrders);
        setLoading(false);
    }, []);

    useEffect(() => {
        if (selectedOrder) {
            setFormData({
                orderNumber: selectedOrder.orderNumber,
                customerName: selectedOrder.customerName,
                orderDate: new Date(selectedOrder.orderDate),
                status: selectedOrder.status,
                priority: selectedOrder.priority,
                totalAmount: selectedOrder.totalAmount,
                itemCount: selectedOrder.itemCount,
                controlStatus: selectedOrder.controlStatus,
                controlDate: selectedOrder.controlDate ? new Date(selectedOrder.controlDate) : new Date(),
                controlledBy: selectedOrder.controlledBy || ''
            });
        } else {
            const today = new Date();
            setFormData({
                orderNumber: '',
                customerName: '',
                orderDate: today,
                status: '',
                priority: '',
                totalAmount: 0,
                itemCount: 0,
                controlStatus: '',
                controlDate: today,
                controlledBy: ''
            });
        }
    }, [selectedOrder]);

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };

        _filters['global'].value = value;

        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const handleEdit = (order) => {
        setSelectedOrder(order);
        setDisplayModal(true);
    };

    const handleSave = () => {
        if (!formData.orderNumber || !formData.customerName) {
            return; // Basic validation
        }

        if (selectedOrder) {
            // Update existing order
            const updatedOrders = orders.map((order) =>
                order.id === selectedOrder.id
                    ? {
                          ...order,
                          controlStatus: formData.controlStatus,
                          controlDate: formatDateForApi(formData.controlDate),
                          controlledBy: formData.controlledBy
                      }
                    : order
            );
            setOrders(updatedOrders);
        }

        setDisplayModal(false);
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="flex gap-2">
                <i onClick={() => handleEdit(rowData)} className="pi pi-pencil" style={{ left: '1.5rem', color: '#0B3187' }}></i>
            </div>
        );
    };

    const statusBodyTemplate = (rowData) => {
        let severity = '';

        switch (rowData.controlStatus) {
            case 'Approved':
                severity = 'success';
                break;
            case 'Pending':
                severity = 'warning';
                break;
            case 'Rejected':
                severity = 'danger';
                break;
            default:
                severity = 'info';
        }

        return <span className={`p-badge p-badge-${severity}`}>{getStatusLabel(rowData.controlStatus)}</span>;
    };

    const getStatusLabel = (code) => {
        const option = statusOptions.find((o) => o.code === code);
        return option ? option.name : code;
    };

    const getPriorityLabel = (code) => {
        const option = priorityOptions.find((o) => o.code === code);
        return option ? option.name : code;
    };

    const header = (
        <div className="flex justify-content-between align-items-center">
            <div className="flex align-items-center">
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Ara..." />
                </span>
            </div>
            <div className="flex gap-2">
                <Button label="Excel Export" icon="pi pi-file-excel" severity="success" />
            </div>
        </div>
    );

    const footerContent = (
        <div className="flex justify-content-end gap-2">
            <Button label="İptal" icon="pi pi-times" outlined onClick={() => setDisplayModal(false)} />
            <Button label="Kaydet" icon="pi pi-check" onClick={handleSave} />
        </div>
    );

    return (
        <div className="card">
            <div className="flex justify-content-between align-items-center mb-4">
                <h1 className="m-0">Sipariş Kontrol Raporu</h1>
            </div>

            <Card>
                <DataTable
                    value={orders}
                    paginator
                    className="p-datatable-gridlines"
                    showGridlines
                    rows={10}
                    dataKey="id"
                    loading={loading}
                    filters={filters}
                    filterDisplay="menu"
                    globalFilterFields={['orderNumber', 'customerName', 'controlStatus', 'controlledBy']}
                    header={header}
                    emptyMessage="Sipariş kontrol kaydı bulunamadı."
                    rowsPerPageOptions={[5, 10, 25, 50, 100, 500, 1000]}
                    tableStyle={{ minWidth: '50rem' }}
                >
                    <Column sortable bodyClassName="text-center" headerClassName="text-center" body={actionBodyTemplate} exportable={false} style={{ width: '5%', minWidth: '33px' }}></Column>
                    <Column field="orderNumber" header="Sipariş No" filter filterPlaceholder="Sipariş no ara..." sortable style={{ width: '150px' }} />
                    <Column field="customerName" header="Müşteri" filter filterPlaceholder="Müşteri ara..." sortable style={{ width: '200px' }} />
                    <Column field="orderDate" header="Sipariş Tarihi" body={(rowData) => dateOnlyBodyTemplate(rowData, 'orderDate')} sortable style={{ width: '150px' }} />
                    <Column
                        field="priority"
                        header="Öncelik"
                        body={(rowData) => getPriorityLabel(rowData.priority)}
                        filter
                        filterElement={(options) => <Dropdown value={options.value} options={priorityOptions} onChange={(e) => options.filterCallback(e.value)} optionLabel="name" optionValue="code" placeholder="Tümü" className="p-column-filter" />}
                        sortable
                        style={{ width: '120px' }}
                    />
                    <Column field="totalAmount" header="Toplam Tutar" body={(rowData) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(rowData.totalAmount)} sortable style={{ width: '150px' }} />
                    <Column field="itemCount" header="Kalem Sayısı" sortable style={{ width: '120px' }} />
                    <Column
                        field="controlStatus"
                        header="Kontrol Durumu"
                        body={statusBodyTemplate}
                        filter
                        filterElement={(options) => <Dropdown value={options.value} options={statusOptions} onChange={(e) => options.filterCallback(e.value)} optionLabel="name" optionValue="code" placeholder="Tümü" className="p-column-filter" />}
                        sortable
                        style={{ width: '150px' }}
                    />
                    <Column field="controlDate" header="Kontrol Tarihi" body={(rowData) => dateOnlyBodyTemplate(rowData, 'controlDate')} sortable style={{ width: '150px' }} />
                    <Column field="controlledBy" header="Kontrol Eden" filter filterPlaceholder="Kontrol eden ara..." sortable style={{ width: '150px' }} />
                </DataTable>
            </Card>

            {/* Edit Modal */}
            <Dialog header="Sipariş Kontrolünü Düzenle" visible={displayModal} style={{ width: '50%' }} modal footer={footerContent} onHide={() => setDisplayModal(false)}>
                <div className="p-fluid grid">
                    <div className="field col-12 md:col-6">
                        <label htmlFor="orderNumber">Sipariş No</label>
                        <InputText id="orderNumber" value={formData.orderNumber} disabled />
                    </div>
                    <div className="field col-12 md:col-6">
                        <label htmlFor="customerName">Müşteri</label>
                        <InputText id="customerName" value={formData.customerName} disabled />
                    </div>
                    <div className="field col-12 md:col-6">
                        <label htmlFor="controlStatus">Kontrol Durumu</label>
                        <Dropdown id="controlStatus" value={formData.controlStatus} options={statusOptions} onChange={(e) => setFormData({ ...formData, controlStatus: e.value })} optionLabel="name" optionValue="code" placeholder="Durum Seçiniz" />
                    </div>
                    <div className="field col-12 md:col-6">
                        <label htmlFor="controlDate">Kontrol Tarihi</label>
                        <Calendar id="controlDate" value={formData.controlDate} onChange={(e) => setFormData({ ...formData, controlDate: e.value })} dateFormat="dd/mm/yy" showIcon />
                    </div>
                    <div className="field col-12">
                        <label htmlFor="controlledBy">Kontrol Eden</label>
                        <InputText id="controlledBy" value={formData.controlledBy} onChange={(e) => setFormData({ ...formData, controlledBy: e.target.value })} />
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default OrderControlReportPage;
